/*
Copyright 2025 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { Room as LivekitRoom } from "livekit-client";
import { type Logger, logger as rootLogger } from "matrix-js-sdk/lib/logger";

import {
  alwaysShowIphoneEarpiece as alwaysShowIphoneEarpieceSetting,
  audioInput as audioInputSetting,
  audioOutput as audioOutputSetting,
  videoInput as videoInputSetting,
} from "../settings/settings";
import { type Scope } from "./Scope";
import { availableOutputDevices$ as controlledAvailableOutputDevices$ } from "../controls";
import { getUrlParams } from "../UrlParams";
import { platform } from "../Platform";
import { type Behavior, computed, constant, state } from "./Behavior";
import { AndroidControlledAudioOutput } from "./AndroidControlledAudioOutput.ts";
import { IOSControlledAudioOutput } from "./IOSControlledAudioOutput.ts";

export type DeviceLabel =
  | { type: "name"; name: string }
  | { type: "number"; number: number };

export type AudioOutputDeviceLabel =
  | DeviceLabel
  | { type: "speaker" }
  | { type: "earpiece" }
  | { type: "default"; name: string | null };

/**
 * Base selected-device value shared by all media kinds.
 *
 * `id` is the effective device identifier used by browser media APIs.
 */
export interface SelectedDevice {
  id: string;
}

/**
 * Selected audio input value with audio-input-specific metadata.
 */
export interface SelectedAudioInputDevice extends SelectedDevice {
  /**
   * Identifies the hardware behind the device. It changes when a device with a
   * stable ID (such as Chrome's "default" device) starts referring to different
   * hardware.
   */
  groupId: string | undefined;
}

/**
 * Selected audio output value with output-routing-specific metadata.
 */
export interface SelectedAudioOutputDevice extends SelectedDevice {
  /**
   * Whether this device is a "virtual earpiece" device. If so, we should output
   * on a single channel of the device at a reduced volume.
   */
  virtualEarpiece: boolean;
}

/**
 * Common reactive contract for selectable input/output media devices (mic, speaker, camera).
 *
 * `Label` is the type used to represent a device in UI lists.
 * `Selected` is the type used to represent the active selection for a device kind.
 */
export interface MediaDevice<Label, Selected> {
  /**
   * Reactive map of currently available devices keyed by device ID.
   *
   * `Label` defines the UI-facing label data structure for each device type.
   */
  available$: Behavior<Map<string, Label>>;
  /**
   * The active device selection.
   * Can be `undefined` when no device is yet selected.
   *
   * When defined, `Selected` contains the selected device ID plus any
   * type-specific metadata.
   */
  selected$: Behavior<Selected | undefined>;
  /**
   * Requests selection of a device by ID.
   *
   * Implementations typically persist this preference and let `selected$`
   * converge to the effective device (which may differ if the requested ID is
   * unavailable).
   */
  select(id: string): void;
}

/**
 * An observable that represents if we should display the devices menu for iOS.
 *
 * This implies the following
 *  - hide any input devices (they do not work anyhow on ios)
 *  - Show a button to show the native output picker instead.
 *  - Only show the earpiece toggle option if the earpiece is available:
 *   `availableOutputDevices$.includes((d)=>d.forEarpiece)`
 */
export const iosDeviceMenu$ =
  platform === "ios" ? constant(true) : alwaysShowIphoneEarpieceSetting.value$;

export function availableRawDevices$(
  kind: MediaDeviceKind,
  usingNames$: Behavior<boolean>,
  scope: Scope,
  logger: Logger,
): Behavior<MediaDeviceInfo[]> {
  const devices$ = state<MediaDeviceInfo[]>([]);
  scope.effect(() => {
    const usingNames = usingNames$.value;
    let stopped = false;
    const refresh = async (): Promise<void> => {
      try {
        let devices = await LivekitRoom.getLocalDevices(kind, false);
        // Requesting device names may prompt for permissions and acquire a
        // media stream, so only do it if names are in fact missing (they may
        // already be known because a media stream is running elsewhere).
        if (usingNames && devices.every((d) => !d.label))
          devices = await LivekitRoom.getLocalDevices(kind, true);
        if (!stopped) devices$.value = devices;
      } catch (e) {
        logger.error("Error enumerating media devices", e);
      }
    };
    void refresh();
    const onDeviceChange = (): void => void refresh();
    navigator.mediaDevices?.addEventListener("devicechange", onDeviceChange);
    return (): void => {
      stopped = true;
      navigator.mediaDevices?.removeEventListener(
        "devicechange",
        onDeviceChange,
      );
    };
  });
  return devices$;
}

function buildDeviceMap(
  availableRaw: MediaDeviceInfo[],
): Map<string, DeviceLabel> {
  return new Map<string, DeviceLabel>(
    availableRaw.map((d, i) => [
      d.deviceId,
      d.label
        ? { type: "name", name: d.label }
        : { type: "number", number: i + 1 },
    ]),
  );
}

function selectDevice<Label>(
  available: Map<string, Label>,
  preferredId: string | undefined,
): string | undefined {
  if (available.size) {
    if (preferredId !== undefined && available.has(preferredId)) {
      // If the preferred device is available, use it.
      return preferredId;
    } else if (available.size === 1 && available.has("")) {
      // In some cases the enumerateDevices will list the devices with empty string details:
      // `{deviceId:'', kind:'audiooutput|audioinput|videoinput', label:'', groupId:''}`
      // This can happen when:
      //     1. The user has not yet granted permissions to microphone/devices
      //     2. The page is not running in a secure context (e.g. localhost or https)
      //     3. In embedded WebViews, restrictions are often tighter, need active capture..
      //     3. The browser is blocking access to device details for privacy reasons (?)
      // This is most likely transitional, so keep the current device selected until we get a more accurate enumerateDevices.
      return preferredId;
    } else {
      // No preferred, so pick a default.
      return available.keys().next().value;
    }
  }
  return undefined;
}

class AudioInput implements MediaDevice<DeviceLabel, SelectedAudioInputDevice> {
  private logger = rootLogger.getChild("[MediaDevices AudioInput]");
  private readonly availableRaw$: Behavior<MediaDeviceInfo[]> =
    availableRawDevices$(
      "audioinput",
      this.usingNames$,
      this.scope,
      this.logger,
    );
  public readonly available$ = computed(() =>
    buildDeviceMap(this.availableRaw$.value),
  );
  public readonly selected$ = computed<SelectedAudioInputDevice | undefined>(
    () => {
      const id = selectDevice(
        this.available$.value,
        audioInputSetting.value$.value,
      );
      return id === undefined
        ? undefined
        : {
            id,
            groupId: this.availableRaw$.value.find((d) => d.deviceId === id)
              ?.groupId,
          };
    },
    (a, b) => a?.id === b?.id && a?.groupId === b?.groupId,
  );

  public select(id: string): void {
    audioInputSetting.setValue(id);
  }

  public constructor(
    private readonly usingNames$: Behavior<boolean>,
    private readonly scope: Scope,
  ) {
    this.scope.effect(() => {
      this.logger.info(
        "[audio-input] available devices:",
        this.available$.value,
      );
    });
  }
}

export class AudioOutput implements MediaDevice<
  AudioOutputDeviceLabel,
  SelectedAudioOutputDevice
> {
  private logger = rootLogger.getChild("[MediaDevices AudioOutput]");
  private readonly availableRaw$ = availableRawDevices$(
    "audiooutput",
    this.usingNames$,
    this.scope,
    this.logger,
  );
  public readonly available$ = computed(() => {
    const availableRaw = this.availableRaw$.value;
    let available: Map<string, AudioOutputDeviceLabel> =
      buildDeviceMap(availableRaw);
    // Create a virtual default audio output for browsers that don't have one.
    // Its device ID must be the empty string because that's what setSinkId
    // recognizes.
    if (available.size && !available.has("") && !available.has("default"))
      available.set("", {
        type: "default",
        name: availableRaw[0]?.label || null,
      });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isSafari = !!(window as any).GestureEvent; // non standard api only found on Safari. https://developer.mozilla.org/en-US/docs/Web/API/GestureEvent#browser_compatibility
    if (isSafari) {
      // set to empty map if we are on Safari, because it does not support setSinkId
      available = new Map();
    }

    // Note: creating virtual default input devices would be another problem
    // entirely, because requesting a media stream from deviceId "" won't
    // automatically track the default device.
    return available;
  });
  public readonly selected$ = computed(() => {
    const id = selectDevice(
      this.available$.value,
      audioOutputSetting.value$.value,
    );
    return id === undefined ? undefined : { id, virtualEarpiece: false };
  });

  public select(id: string): void {
    audioOutputSetting.setValue(id);
  }

  public constructor(
    private readonly usingNames$: Behavior<boolean>,
    private readonly scope: Scope,
  ) {
    this.scope.effect(() => {
      this.logger.info(
        "[audio-output] available devices:",
        this.available$.value,
      );
    });
  }
}

class VideoInput implements MediaDevice<DeviceLabel, SelectedDevice> {
  private logger = rootLogger.getChild("[MediaDevices VideoInput]");
  private readonly availableRaw$ = availableRawDevices$(
    "videoinput",
    this.usingNames$,
    this.scope,
    this.logger,
  );
  public readonly available$ = computed(() =>
    buildDeviceMap(this.availableRaw$.value),
  );
  public readonly selected$ = computed(() => {
    const id = selectDevice(
      this.available$.value,
      videoInputSetting.value$.value,
    );
    return id === undefined ? undefined : { id };
  });

  public select(id: string): void {
    videoInputSetting.setValue(id);
  }

  public constructor(
    private readonly usingNames$: Behavior<boolean>,
    private readonly scope: Scope,
  ) {
    // This also has the purpose of subscribing to the available devices
    this.scope.effect(() => {
      this.logger.info(
        "[video-input] available devices:",
        this.available$.value,
      );
    });
  }
}

export class MediaDevices {
  // Start using device names as soon as requested. This will cause LiveKit to
  // briefly request device permissions and acquire media streams for each
  // device type while calling `enumerateDevices`, which is what browsers want
  // you to do to receive device names in lieu of a more explicit permissions
  // API. This flag never resets to false, because once permissions are granted
  // the first time, the user won't be prompted again until reload of the page.
  private readonly usingNames$ = state(false);

  /**
   * Requests that the media devices be populated with the names of each
   * available device, rather than numbered identifiers. This may invoke a
   * permissions pop-up, so it should only be called when there is a clear user
   * intent to view the device list.
   */
  public requestDeviceNames(): void {
    this.usingNames$.value = true;
  }

  public readonly audioInput: MediaDevice<
    DeviceLabel,
    SelectedAudioInputDevice
  > = new AudioInput(this.usingNames$, this.scope);

  public readonly audioOutput: MediaDevice<
    AudioOutputDeviceLabel,
    SelectedAudioOutputDevice
  > = getUrlParams().controlledAudioDevices
    ? platform == "android"
      ? new AndroidControlledAudioOutput(
          controlledAvailableOutputDevices$,
          this.scope,
          getUrlParams().callIntent,
          window.controls,
        )
      : new IOSControlledAudioOutput(
          this.usingNames$,
          this.scope,
          getUrlParams().callIntent,
        )
    : new AudioOutput(this.usingNames$, this.scope);

  public readonly videoInput: MediaDevice<DeviceLabel, SelectedDevice> =
    new VideoInput(this.usingNames$, this.scope);

  public constructor(private readonly scope: Scope) {}
}
